import {
  IonCol,
  IonGrid,
  IonLabel,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './subscription.css';
import { i18nKeyType } from '../../i18n';
import { SubscriptionPlanCard } from './card/subscription-plan-card';
import { useSubscriptionContext } from './subscription.context';
import { SubscriptionBillingInterval } from './subscription.types';

const BILLING_INTERVAL_SEGMENTS = [
  {
    value: SubscriptionBillingInterval.YEAR,
    label: 'common:billing_interval_yearly',
    enabled: true,
  },
  {
    value: SubscriptionBillingInterval.MONTH,
    label: 'common:billing_interval_monthly',
    enabled: true,
  },
];

const Subscription = () => {
  const { i18n } = useTranslation();

  const [billingInterval, setBillingInterval] = useState(
    SubscriptionBillingInterval.YEAR
  );

  const {
    plans,
    existingLicenses,
    cart,
    hasExistingSubscription,
    setCartBillingInterval,
  } = useSubscriptionContext();

  const plansByInterval = useMemo(() => {
    return plans?.filter(
      (plan) =>
        plan.product?.prices?.some(
          (price) => price.recurringInterval === billingInterval
        )
    );
  }, [plans, billingInterval]);

  const availableIntervals = useMemo(() => {
    // we only support one billing interval per subscription so if the cart
    // has items then take the first billing interval in the cart and disable
    // the others
    const cartBillingInterval = cart?.items[0]?.price?.recurringInterval;

    let licenseBillingInterval: SubscriptionBillingInterval | undefined;

    if (hasExistingSubscription) {
      // if the user has an existing subscription then take the billing interval
      // from one of the licenses

      const license = existingLicenses[0];

      if (license) {
        const price = plans
          ?.find((plan) => plan.id === license.planId)
          ?.product?.prices?.find((p) => p.id === license.priceId);

        if (price) {
          licenseBillingInterval = price.recurringInterval;

          // make sure we set the billing interval to the one from the license
          setBillingInterval(price.recurringInterval);
        }
      }
    }

    const intervals = BILLING_INTERVAL_SEGMENTS.map((segment) => {
      return {
        ...segment,
        enabled:
          segment.value === cartBillingInterval ||
          segment.value === licenseBillingInterval ||
          (!cartBillingInterval && !licenseBillingInterval),
      };
    });

    return intervals;
  }, [cart, plans, existingLicenses, hasExistingSubscription]);

  useEffect(() => {
    // make sure cart always reflects billing interval of the current
    // interval selection
    setCartBillingInterval(billingInterval);
  }, [billingInterval, setCartBillingInterval]);

  return (
    <>
      <div className="subscription-header">
        <div className="pricing-interval-segment">
          <IonToolbar>
            <IonSegment
              value={billingInterval}
              onIonChange={(e) =>
                setBillingInterval(
                  e.detail.value as SubscriptionBillingInterval
                )
              }
            >
              {availableIntervals.map((segment) => (
                <IonSegmentButton
                  key={segment.value}
                  value={segment.value}
                  disabled={!segment.enabled}
                >
                  <IonLabel>{i18n.t(segment.label as i18nKeyType)}</IonLabel>
                </IonSegmentButton>
              ))}
            </IonSegment>
          </IonToolbar>
        </div>
      </div>
      <IonGrid>
        <IonRow class="ion-align-items-stretch">
          {plansByInterval?.map((plan) => {
            return (
              <IonCol key={plan.id} size="6" sizeXs="12" sizeLg="6" sizeXl="6">
                <SubscriptionPlanCard
                  plan={plan}
                  billingInterval={billingInterval}
                  license={existingLicenses?.find(
                    (existingLicense) => existingLicense.planId === plan.id
                  )}
                  isInCart={cart.items.some((i) => i.plan.id === plan.id)}
                />
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
    </>
  );
};

export { Subscription };
